import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone } from "lucide-react";
import { COMPANY, ADDRESS_LINES } from "@/lib/company";
import { BrandMark } from "@/components/shared/BrandMark";

export const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-[color:var(--sg-border)] bg-white mt-16" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <Link to="/" className="flex items-center gap-2.5 mb-3" data-testid="footer-logo">
            <BrandMark
              showIcon
              iconClassName="h-8 w-8 mr-2 inline-block align-middle dark:invert"
              className="font-serif-display text-lg font-bold tracking-tight text-[color:var(--sg-fg)] flex items-center"
            />
          </Link>
          <p className="text-sm text-[color:var(--sg-muted)] leading-relaxed max-w-xs">
            Capture social signals. Turn them into action. Discovery, publishing and comment intelligence — in one workspace.
          </p>
          <div className="mt-4 space-y-1.5 text-xs text-[color:var(--sg-muted)]">
            <a
              href={`mailto:${COMPANY.supportEmail}`}
              data-testid="footer-support-email"
              className="inline-flex items-center gap-1.5 text-sm text-[color:var(--sg-primary)] hover:underline"
            >
              <Mail className="h-3.5 w-3.5" /> {COMPANY.supportEmail}
            </a>
            <a
              href={`tel:${COMPANY.customerCarePhoneRaw}`}
              data-testid="footer-support-phone"
              className="flex items-center gap-1.5 text-sm text-[color:var(--sg-primary)] hover:underline"
            >
              <Phone className="h-3.5 w-3.5" /> {COMPANY.customerCarePhone}
            </a>
          </div>
        </div>

        <div>
          <div className="sg-label mb-3">Product</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/signup" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]">Get started</Link></li>
            <li><Link to="/login" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]">Log in</Link></li>
            <li><Link to="/shop" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-shop">Shop</Link></li>
            <li><Link to="/ai-cart" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-ai-cart">AI Cart™ Search</Link></li>
            <li><Link to="/pricing" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-pricing">Pricing</Link></li>
            <li><Link to="/gift" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-gift">🎁 Surprise Gift</Link></li>
            <li><Link to="/affiliate" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-affiliate">Affiliate Program</Link></li>
            <li><Link to="/founder-story" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-founder-story">Founder Story</Link></li>
            <li><Link to="/about" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-about">About</Link></li>
            <li><Link to="/contact" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-contact">Contact</Link></li>
          </ul>
        </div>

        <div>
          <div className="sg-label mb-3">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/terms" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-terms">Terms of Service</Link></li>
            <li><Link to="/cookies" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-cookies">Cookie Policy</Link></li>
            <li><Link to="/refunds" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-refunds">Refund Policy</Link></li>
          </ul>
        </div>

        <div>
          <div className="sg-label mb-3">Data</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/data-deletion" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-data-deletion">Data Deletion</Link></li>
            <li><Link to="/security" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-security">Security</Link></li>
            <li><Link to="/dpa" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]" data-testid="footer-dpa">DPA</Link></li>
          </ul>
        </div>
      </div>

      {/* Company registration block */}
      <div className="border-t border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6" data-testid="footer-company-info">
          <div className="grid md:grid-cols-3 gap-6 text-xs">
            <div>
              <div className="sg-label mb-1.5">Registered entity</div>
              <div className="text-[color:var(--sg-fg)] font-semibold">{COMPANY.legalName}</div>
              <div className="text-[color:var(--sg-muted)] mt-1">CIN: <span className="font-mono text-[color:var(--sg-fg)]">{COMPANY.cin}</span></div>
              <div className="text-[color:var(--sg-muted)]">GST: <span className="font-mono text-[color:var(--sg-fg)]">{COMPANY.gst}</span></div>
            </div>
            <div>
              <div className="sg-label mb-1.5">Registered office</div>
              {ADDRESS_LINES.map((l, i) => (
                <div key={i} className="text-[color:var(--sg-muted)] leading-snug">{l}</div>
              ))}
            </div>
            <div>
              <div className="sg-label mb-1.5">Customer care</div>
              <a href={`tel:${COMPANY.customerCarePhoneRaw}`} className="flex items-center gap-1.5 text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)]">
                <Phone className="h-3 w-3" /> {COMPANY.customerCarePhone}
              </a>
              <a href={`mailto:${COMPANY.supportEmail}`} className="flex items-center gap-1.5 text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)] mt-1">
                <Mail className="h-3 w-3" /> {COMPANY.supportEmail}
              </a>
              <div className="text-[10px] text-[color:var(--sg-muted)] mt-2">
                {COMPANY.supportHours}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[color:var(--sg-border)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-[color:var(--sg-muted)]">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>© {year} {COMPANY.brand}</span>
            <span>·</span>
            <span
              data-testid="footer-made-in-india"
              className="inline-flex items-center gap-1.5 rounded-full border border-orange-500/40 bg-gradient-to-r from-orange-500/10 to-emerald-500/10 px-2.5 py-1 font-bold text-[color:var(--sg-fg)] uppercase tracking-widest text-[10px]"
            >
              <span aria-hidden className="text-base leading-none">🇮🇳</span>
              Made in India
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to="/privacy" className="hover:text-[color:var(--sg-fg)]">Privacy</Link>
            <Link to="/terms" className="hover:text-[color:var(--sg-fg)]">Terms</Link>
            <Link to="/cookies" className="hover:text-[color:var(--sg-fg)]">Cookies</Link>
            <Link to="/data-deletion" className="hover:text-[color:var(--sg-fg)]">Data deletion</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
